<template>
    <div class="page page--gradient-background">
        <div class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header class="row__header">
                    <back-button
                        :isAbsolute="false"
                        :isBlack="true"
                        :isHiddenOnDesktop="false"
                    />
                    <h1>{{ $t('settings.linkedApps.manageAccess') }}</h1>
                </header>

                <loading-spinner v-if="loading" />

                <section v-else-if="showLinkedApps"
                    class="content"
                >
                    <div class="content__title">{{ $t('settings.linkedApps.title') }}</div>

                    <div class="content__apps">
                        <div v-for="(app, index) in organizations"
                            :key="index"
                            :class="['content__apps__entry', {'maximized' : index === maximizedApp}]"
                        >
                            <div class="content__apps__entry--top"
                                @click="toggleAppVisibility(index)"
                            >
                                <div class="app__name">{{ app.organizationName }}</div>
                                <i class="icon-chevron app__toggler" />
                            </div>
                            <div class="content__apps__entry--bottom">
                                <div class="app__consents">
                                    <div class="app__consents__description">{{ $t('settings.linkedApps.content.consentList') }}</div>
                                    <consent-list :list="app.consentType" />
                                </div>
                                <div class="app__access-given">
                                    <div class="app__access-given__description">{{ $t('settings.linkedApps.content.consentDate') }}</div>
                                    <span class="app__access-given__data">{{ app.updatedAt }}</span>
                                </div>
                                <div class="app__revoke">
                                    <div class="app__revoke__button widget__button"
                                        @click="displayConfirmationModal(app.organizationId)"
                                    >
                                        {{ $t('settings.linkedApps.remove.button') }}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="content__description">{{ $t('settings.linkedApps.description') }}</div>
                </section>

                <section v-else
                    class="empty"
                >
                    <img :src="require(`@webapp/assets/images/mascot/error.webp`)"
                        class="empty__image"
                        :alt="$t('settings.linkedApps.empty')"
                    >
                    <div class="empty__description">{{ $t('settings.linkedApps.empty') }}</div>
                </section>
            </div>
        </div>
    </div>
</template>

<script>
import {
    mapActions,
} from 'vuex';

// Components
import backButton from '@webapp/components/back-button/back-button.vue';
import consentList from '@webapp/modules/partner/components/lists/consent-list.vue';
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';

// Utils
import {
    format,
} from '@shared/utils/dates.js';

import {
    api,
} from '@shared/setups/api';

// Local Types
import localStoreTypes from '@webapp/store/types.js';

export default {
    name: 'manage-access',
    components: {
        backButton,
        consentList,
        loadingSpinner,
    },
    data() {
        return {
            maximizedApp: null,
            organizations: [],
            loading: true,
        };
    },
    computed: {
        showLinkedApps() {
            return Boolean(this.organizations.length);
        },
    },
    created() {
        this.populateUserConsents();
    },
    methods: {
        ...mapActions({
            setModal: localStoreTypes.SET_MODAL,
        }),

        toggleAppVisibility(i) {
            this.maximizedApp = i === this.maximizedApp ? null : i;
        },

        formattedConsentAcceptedDate(date) {
            return format(date, 'longWeekdayDayMonthYear');
        },

        displayConfirmationModal(organizationId) {
            const organizationConsents = this.getOrganizationConsentIds(organizationId);

            this.setModal({
                isVisible: true,
                isClosable: true,
                componentToLoad: 'modules/partner/components/modals/remove-consent',
                data: {
                    organizationConsentIdList: organizationConsents,
                },
                onDestroy: () => {
                    this.loading = true;
                    this.organizations = [];
                    this.populateUserConsents();
                },
            });
        },

        getOrganizationConsentIds(organizationId) {
            const organizationConsentIdList = [];
            const organization = this.organizations.find((organization) => organization.organizationId === organizationId);

            organization.consentType.forEach((consent) => organizationConsentIdList.push(consent.consentId));

            return organizationConsentIdList;
        },

        async populateUserConsents() {
            const consentList = await api.getUserConsents({
                details: ['consentTypeImages'],
            });

            if (consentList) {
                const allowedConsentList = this.removeUnallowedConsents(consentList.data);

                if (allowedConsentList) {
                    this.filterConsentsByOrganization(allowedConsentList);
                }
            }

            this.loading = false;
        },

        removeUnallowedConsents(consentList) {
            return consentList.filter((consent) => consent.allowed === true);
        },

        filterConsentsByOrganization(consentList) {
            consentList.forEach((consent) => {
                if (this.organizations.find((organization) => organization.organizationId === consent.consentOrganization.organization.id)) {
                    const organizationIndex = this.organizations.findIndex((organization) => organization.organizationId ===
                        consent.consentOrganization.organization.id);

                    const newConsent = {
                        title: consent.consentOrganization.consentType.title,
                        description: consent.consentOrganization.consentType.description,
                        consentId: consent.id,
                        allowed: consent.allowed,
                        images: consent.consentOrganization.consentType.images,
                    };

                    this.organizations[organizationIndex].consentType.push(newConsent);
                } else {
                    const newOrganization = {
                        organizationId: consent.consentOrganization.organization.id,
                        organizationName: consent.consentOrganization.organization.label,
                        organizationWso2Label: consent.consentOrganization.organization.wso2Label,
                        createdAt: this.formattedConsentAcceptedDate(consent.createdAt),
                        updatedAt: this.formattedConsentAcceptedDate(consent.updatedAt),
                        consentType: [
                            {
                                title: consent.consentOrganization.consentType.title,
                                description: consent.consentOrganization.consentType.description,
                                consentId: consent.id,
                                allowed: consent.allowed,
                                images: consent.consentOrganization.consentType.images,
                            },
                        ],
                    };

                    this.organizations.push(newOrganization);
                }
            });
        },
    },
};
</script>

<style lang="scss" scoped>
.page {
    padding-bottom: 7rem;

    @include min-width(md) {
        padding-bottom: 0;
    }
}

.row__header {
    padding-bottom: 5rem;
}

.content {
    padding: 0 2rem 4rem;
    color: $blue-charcoal-color;
    font-size: 1.7rem;

    @include min-width(md) {
        padding: 0 6rem 4rem;
    }

    &__title {
        font-size: 1.7rem;
        line-height: 2.7rem;
        font-weight: 700;
        color: $cyprus-color;
        padding-bottom: 1rem;

        @include min-width(lg) {
            font-size: 2.2rem;
        }
    }

    &__apps {
        background-color: $white-color;
        border-radius: 8px;

        &__entry {
            border-bottom: 1px solid rgba($hit-gray-color, 0.3);
            box-sizing: border-box;
            max-height: 70px;
            overflow: hidden;
            transition: 300ms ease-in-out;

            &--top {
                padding: 22px 20px;
                display: flex;
                justify-content: space-between;
                font-weight: 600;
                align-items: center;
                cursor: pointer;

                .app__toggler {
                    font-size: 0.7em;
                    transition: 150ms;
                    transform: rotate(180deg);
                }

                &:hover {
                    .app__name {
                        color: $lima-color;
                        transition: 150ms;
                    }

                    .app__toggler {
                        transition: 150ms;
                        color: $lima-color;
                    }
                }
            }

            &--bottom {
                margin-top: 10px;
                padding: 0 20px 22px;

                .app__consents {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 20px;

                    &__description {
                        flex: 1;
                    }

                    &__entries {
                        flex: 3;
                    }

                    @include max-width(lg) {
                        flex-direction: column;

                        &__description {
                            font-weight: 600;
                        }

                        &__description,
                        &__entries {
                            flex: 1;
                            width: 100%;
                            margin-bottom: 20px;
                        }
                    }
                }

                .app__access-given {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;

                    @include min-width(lg) {
                        &__description {
                            flex: 1;
                        }

                        &__data {
                            flex: 3;
                        }
                    }

                    @include max-width(lg) {
                        &__description {
                            font-weight: 600;
                        }
                    }

                    @include max-width(sm) {
                        flex-direction: column;
                    }
                }

                .app__revoke {
                    display: flex;
                    justify-content: flex-end;

                    &__button {
                        width: fit-content;
                        padding-left: 40px;
                        padding-right: 40px;
                        color: $white-color;
                        font-weight: 600;
                        background-color: $torch-red-color;
                    }

                    @include max-width(md) {
                        justify-content: center;
                    }

                    @include max-width(sm) {
                        &__button {
                            text-align: center;
                            width: 100%;
                        }
                    }
                }
            }

            &:last-child {
                border-bottom: none;
            }
        }

        .maximized {
            max-height: 750px;
            transition: max-height 300ms ease-in-out;
            box-shadow: 0 4px 10px 1px rgba($hit-gray-color, 0.1);
            border-radius: 8px;
            overflow-y: auto;
            animation: hide-scroll 300ms backwards;

            .app__toggler {
                transform: rotate(0deg);
                transition: 150ms;
                color: $lima-color;
            }

            .app__name {
                font-weight: 700;
                transition: 150ms;
                color: $lima-color;
            }
        }
    }

    &__description {
        margin-top: 20px;
        font-size: 1.4rem;
    }
}

.empty {
    text-align: center;
    color: $blue-charcoal-color;

    &__image {
        width: 35em;
        height: auto;
        margin-bottom: 20px;
    }

    &__description {
        font-size: 1.7rem;
        padding: 0 6rem 4rem;
    }
}

@keyframes hide-scroll {
    from,
    to {
        overflow: hidden;
    }
}
</style>